import { useMemo } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { topics, formatHeat, type Topic } from "@/mocks/topics";
import TrendBadge from "@/components/base/TrendBadge";
import SectionHeading from "@/components/base/SectionHeading";

interface RankSectionProps {
  activeCategory: string;
  onCategoryChange: (id: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
}

function RankItem({ topic, index }: { topic: Topic; index: number }) {
  return (
    <article data-product-shop className="group">
      <Link
        to={`/topic/${topic.id}`}
        className="flex gap-4 rounded-lg border border-transparent px-3 py-4 transition-colors hover:border-background-200 hover:bg-background-50 md:gap-5 md:px-4"
      >
        <div className="flex w-9 shrink-0 flex-col items-center md:w-11">
          <span
            className={
              index === 0
                ? "font-heading text-2xl font-black text-primary-600 md:text-3xl"
                : index < 3
                  ? "font-heading text-2xl font-black text-accent-600 md:text-3xl"
                  : "font-heading text-2xl font-bold text-foreground-300 md:text-3xl"
            }
          >
            {String(topic.rank).padStart(2, "0")}
          </span>
          <span className="mt-1 text-[10px] font-label text-foreground-400">名</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <TrendBadge trend={topic.trend} value={topic.trendValue} />
            {topic.live && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-label font-medium text-primary-700">
                <span className="flex h-1.5 w-1.5 items-center justify-center rounded-full bg-primary-500"></span>
                实时
              </span>
            )}
            {topic.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-50 px-2 py-0.5 text-xs font-label text-secondary-700"
              >
                {tag}
              </span>
            ))}
          </div>

          <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {topic.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {topic.summary}
          </p>

          <div className="mt-3 flex items-center gap-4">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
              <div
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${Math.min(100, (topic.heat / topics[0].heat) * 100)}%` }}
              ></div>
            </div>
            <span className="flex shrink-0 items-center gap-1 font-label text-xs font-semibold text-foreground-700 md:text-sm">
              <i className="ri-fire-line text-primary-500"></i>
              {formatHeat(topic.heat)}
            </span>
          </div>

          <div className="mt-2 flex items-center gap-4 text-xs font-label text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {topic.comments} 讨论
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {topic.updatedAt}
            </span>
          </div>
        </div>

        <div className="hidden items-center pr-1 text-foreground-300 transition-colors group-hover:text-primary-500 md:flex">
          <i className="ri-arrow-right-s-line text-xl"></i>
        </div>
      </Link>
    </article>
  );
}

export default function RankSection({
  activeCategory,
  onCategoryChange,
  query,
  onQueryChange,
}: RankSectionProps) {
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return topics.filter((topic) => {
      const matchCategory = activeCategory === "all" || topic.category === activeCategory;
      const matchQuery =
        !keyword ||
        topic.title.toLowerCase().includes(keyword) ||
        topic.summary.toLowerCase().includes(keyword) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchQuery;
    });
  }, [activeCategory, query]);

  const rising = [...topics]
    .filter((t) => t.trend === "up")
    .sort((a, b) => b.trendValue - a.trendValue)
    .slice(0, 3);
  const fresh = topics.filter((t) => t.trend === "new");

  return (
    <section id="rank" className="w-full bg-background-100">
      <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="实时总榜"
          title="热门吃瓜榜 · 今日焦点排行"
          desc="按讨论量、转发增速与持续时长综合排位。切换分类或搜索关键词，快速定位你关心的焦点话题。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => onCategoryChange(cat.id)}
                className={
                  activeCategory === cat.id
                    ? "cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-label font-medium text-background-50 transition-colors"
                    : "cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-label text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                }
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="搜索榜单话题 / 关键词"
              aria-label="搜索榜单话题"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-2 md:p-3">
              <div className="flex items-center justify-between px-2 py-2">
                <span className="text-xs font-label text-foreground-500">
                  共 {filtered.length} 条结果 · 更新于几分钟前
                </span>
                <span className="text-xs font-label text-foreground-400">热度按实时数据计算</span>
              </div>
              {filtered.length > 0 ? (
                <div className="divide-y divide-background-200">
                  {filtered.map((topic, index) => (
                    <RankItem key={topic.id} topic={topic} index={index} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <p className="font-label text-sm text-foreground-600">
                    没有找到匹配「{query}」的话题
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onQueryChange("");
                      onCategoryChange("all");
                    }}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="flex flex-col gap-5 lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-arrow-up-double-line"></i>
                  </span>
                  涨热榜
                </h3>
                <ul className="mt-4 space-y-3">
                  {rising.map((topic) => (
                    <li key={topic.id}>
                      <Link
                        to={`/topic/${topic.id}`}
                        className="flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                      >
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-500 text-xs font-bold text-background-50">
                          {topics.findIndex((t) => t.id === topic.id) + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800">
                            {topic.title}
                          </span>
                          <span className="mt-1 flex items-center gap-1 text-xs font-label text-primary-600">
                            <i className="ri-arrow-up-line"></i>+{topic.trendValue} 热度
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-sparkling-line"></i>
                  </span>
                  新上榜
                </h3>
                <ul className="mt-4 space-y-2">
                  {fresh.map((topic) => (
                    <li key={topic.id}>
                      <Link
                        to={`/topic/${topic.id}`}
                        className="flex items-start gap-2 rounded-md p-2 transition-colors hover:bg-background-100"
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                          <i className="ri-flashlight-line"></i>
                        </span>
                        <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800">
                          {topic.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-secondary-50 p-5">
                <h3 className="font-heading text-base font-bold text-secondary-900">围观小提示</h3>
                <p className="mt-2 text-sm leading-relaxed text-secondary-800">
                  榜单只是入口。点进话题详情可以看到事件回顾时间线与网友热评，先看清楚再发言，围观体验更好。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}